import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PermissionGuard,
  NoPermissionFallback,
} from "../../components/PermissionGuard";
import { CreateUserData, ApiKeyOption, ApiKeyResponse } from "../../interfaces/user";

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateUserData>({
    email: "",
    password: "",
    name: "",
    role: "user",
    apikey: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKeyOption[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);

  // 載入可用的 API Keys
  useEffect(() => {
    const fetchApiKeys = async () => {
      setLoadingApiKeys(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
        const response = await fetch(`${API_URL}/auth/available-apikeys`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data: ApiKeyResponse = await response.json();
          setApiKeys(data.data.apiKeys);
        }
      } catch (error) {
        console.error("載入 API Keys 失敗:", error);
      } finally {
        setLoadingApiKeys(false);
      }
    };

    fetchApiKeys();
  }, []);

  // 處理表單輸入變更
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 處理選擇框變更
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 驗證表單
  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return "姓名為必填項目";
    }

    if (!formData.email.trim()) {
      return "電子郵件為必填項目";
    }

    // 簡單的電子郵件格式驗證
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "請輸入有效的電子郵件格式";
    }

    if (!formData.password) {
      return "密碼為必填項目";
    }

    if (formData.password.length < 8) {
      return "密碼必須至少 8 個字元";
    }

    // 密碼強度檢查
    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumbers = /\d/.test(formData.password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      return "密碼必須包含大寫字母、小寫字母和數字";
    }

    if (formData.password !== confirmPassword) {
      return "密碼與確認密碼不相符";
    }

    return null;
  };

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 驗證表單
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("未找到授權令牌，請重新登入");
      }

      const API_URL =
        import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";

      const response = await fetch(`${API_URL}/auth/create-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP ${response.status}: 建立用戶失敗`
        );
      }

      if (data.success) {
        setSuccess(true);
        // 3 秒後自動導向用戶列表
        setTimeout(() => {
          navigate("/users");
        }, 3000);
      } else {
        throw new Error(data.message || "建立用戶失敗");
      }
    } catch (err) {
      console.error("建立用戶錯誤:", err);
      setError(err instanceof Error ? err.message : "建立用戶失敗，請稍後再試");
    } finally {
      setIsLoading(false);
    }
  };

  // 成功頁面
  if (success) {
    return (
      <PermissionGuard
        requiredRoles={["admin", "maintainer"]}
        fallback={<NoPermissionFallback />}
      >
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  用戶建立成功！
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  新用戶 "{formData.name}"
                  已成功建立，系統將自動導向用戶列表頁面。
                </p>
                <div className="mt-6 flex flex-col space-y-3">
                  <Link
                    to="/users"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    查看用戶列表
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setSuccess(false);
                      setFormData({ email: "", password: "", name: "", role: "user", apikey: "" });
                      setConfirmPassword("");
                    }}
                    className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    繼續建立用戶
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      requiredRoles={["admin", "maintainer"]}
      fallback={<NoPermissionFallback />}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        {/* 面包屑導航 */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div>
                <Link to="/users" className="text-gray-400 hover:text-gray-500">
                  <svg
                    className="flex-shrink-0 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="sr-only">返回</span>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <Link
                  to="/users"
                  className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  用戶管理
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-4 text-sm font-medium text-gray-500">
                  建立用戶
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* 頁面標題 */}
        <div className="mt-6">
          <h1 className="text-2xl font-bold text-gray-900">建立新用戶</h1>
          <p className="mt-2 text-sm text-gray-600">
            建立新的用戶帳號，新用戶將獲得一般用戶權限，可以使用帳號密碼登入系統。
          </p>
        </div>

        {/* 建立表單 */}
        <div className="mt-8 max-w-lg">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 姓名 */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="請輸入用戶姓名"
                    />
                  </div>
                </div>

                {/* 角色 */}
                <div>
                  <label
                    htmlFor="role"
                    className="block text-sm font-medium text-gray-700"
                  >
                    角色 <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <select
                      id="role"
                      name="role"
                      value={formData.role || "user"}
                      onChange={handleSelectChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="user">一般用戶 (User)</option>
                      <option value="maintainer">維護人員 (Maintainer)</option>
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    一般用戶僅能檢視，維護人員可提出修改建議
                  </p>
                </div>

                {/* 電子郵件 */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    電子郵件 <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="請輸入電子郵件地址"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    用戶將使用此電子郵件地址登入系統
                  </p>
                </div>

                {/* 密碼 */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    密碼 <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="請輸入密碼"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    密碼必須至少 8 個字元，包含大寫字母、小寫字母和數字
                  </p>
                </div>

                {/* 確認密碼 */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    確認密碼 <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="請再次輸入密碼"
                    />
                  </div>
                </div>

                {/* API Key 選擇 */}
                <div>
                  <label
                    htmlFor="apikey"
                    className="block text-sm font-medium text-gray-700"
                  >
                    API Key
                  </label>
                  <div className="mt-1">
                    <select
                      id="apikey"
                      name="apikey"
                      value={formData.apikey || ""}
                      onChange={handleSelectChange}
                      disabled={loadingApiKeys}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">請選擇 API Key (可選)</option>
                      {apiKeys.map((option) => (
                        <option key={option.apiKey} value={option.apiKey}>
                          {option.projectName} ({option.apiKey.substring(0, 8)}...)
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {loadingApiKeys 
                      ? "載入中..."
                      : apiKeys.length > 0 
                        ? "選擇用戶可存取的專案 API Key（可選）"
                        : "目前沒有可用的 API Key"
                    }
                  </p>
                </div>

                {/* 角色說明 */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        用戶權限說明
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>新建立的用戶將自動獲得「一般用戶」權限，可以：</p>
                        <ul className="list-disc list-inside mt-1">
                          <li>查看 IMDF 檔案列表和詳情</li>
                          <li>下載 IMDF 檔案</li>
                          <li>查看專案資訊</li>
                          <li>修改個人資料</li>
                        </ul>
                        <p className="mt-2 font-medium">關於 API Key：</p>
                        <ul className="list-disc list-inside mt-1">
                          <li>如果設定 API Key，用戶只能查看對應專案的 IMDF 檔案</li>
                          <li>如果不設定 API Key，需要管理員稍後手動指定</li>
                          <li>API Key 可以在建立後由管理員修改</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 提交按鈕 */}
                <div className="flex justify-end space-x-3">
                  <Link
                    to="/users"
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    取消
                  </Link>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        建立中...
                      </>
                    ) : (
                      "建立用戶"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default UserCreatePage;
